import {ISheetCellFormat, ISheetCellSettings} from '@mintdata/mint-kit/api/functions/sheet/SheetCellSettings';
import {BoxId, SheetId, SlotId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {Empty, IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {AStringEnum} from '@mintdata/mint-kit/api/values/AStringEnum';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';

export type SheetUniqueId = string;

export class FormulaKind extends AStringEnum
{
    public constructor(value:AString)
    {
        super(value, FormulaKind._types);
    }

    public toString():string
    {
        return this.value ? this.value.toString() : null;
    }

    public static _A1 = new AString('A1');
    public static _RC = new AString('RC');

    public static _types:Array<AString> = [
        FormulaKind._A1,
        FormulaKind._RC
    ];

    public static A1():FormulaKind
    {
        return new FormulaKind(FormulaKind._A1);
    }

    public static RC():FormulaKind
    {
        return new FormulaKind(FormulaKind._RC);
    }
}

export enum SheetType
{
    SheetLocation = 'SheetLocation',
    SheetRange = 'SheetRange',
    SheetRangeList = 'SheetRangeList',
    FormulaExpression = 'FormulaExpression',
    AnyIValue = 'AnyIValue',
    Any = 'Any',
    SheetAPI = 'SheetAPI',
}

export class SheetValueType
{
    private static AllSheetTypes:SheetType[] = [
        SheetType.SheetRangeList,
        SheetType.SheetRange,
        SheetType.SheetLocation,
        SheetType.SheetAPI
    ];

    public static isInstanceOf(object:any, type:SheetType):boolean
    {
        if( !object || !type )
            return false;

        return typeof object.getType === 'function'
            && type === object.getType();
    }

    public static isInstanceOfSheetValue(object:any):boolean
    {
        if( !object || typeof object.getType !== 'function' )
            return false;

        const type:SheetType = object.getType();

        for( let aType of this.AllSheetTypes )
        {
            if( aType === type )
                return true;
        }

        return false;
    }
}

// marker interface
export interface ISheetValue
{
    getType():SheetType
}

export type SheetLocationKey = string;

export interface ISheetLocation extends ISheetValue
{
    readonly sheetId:SheetUniqueId;
    readonly columnIndex:number;
    readonly rowIndex:number;

    toA1Location():string;

    asKey():SheetLocationKey;
}

export interface ISheetRange extends ISheetValue
{
    readonly sheetId:SheetUniqueId;
    readonly from:ISheetLocation;
    readonly to:ISheetLocation;

    isVector():boolean;
    contains(location:ISheetLocation):boolean;
}

export interface ISheetRangeList extends ISheetValue
{
    readonly ranges:ISheetRange[];
}

export type NamedArguments = Map<string, IValue | ISheetValue>;

export interface IFormulaExpression extends ISheetValue
{
    eval:(namedArguments?:NamedArguments) => Promise<any>;
}

export interface ISheetRangeIterator
{
    readonly numColumns:number;
    readonly numRows:number;
    readonly numCells:number;

    getNthCellLocation(offset:number):ISheetLocation;

    getNthCellValue(offset:number):IValue;

    getValueAtOffset(xOffset:number, yOffset:number):IValue;

    applyToEachSlotInRange(consumer:(sheetLocation:ISheetLocation) => void):void;
}

export enum SheetDirection
{
    Down,
    Up,
    Left,
    Right
}

export enum SheetRangeSide
{
    InsideRange,
    OutsideRange
}

export interface INamingContext
{
    getNextColumnName():string;
}

export class SimpleNamingContext implements INamingContext
{
    private _startIndex:number = 1;

    constructor()
    {
    }

    getNextColumnName():string
    {
        return `column${this._startIndex++}`;
    }
}

export type IsCellReEvaluationDone = boolean;

export enum AnimationType
{
    Tween
}

export interface ISheetContext
{
    isArrayFormula():boolean;

    isLiveCell():boolean;

    getCellValue(location:ISheetLocation):IValue;

    getCellStatus(location:ISheetLocation):IValue;

    isCellLoading(location:ISheetLocation):boolean;

    isCellStreaming(location:ISheetLocation):boolean;

    getRangeValue(range:ISheetRange, autoRows?:boolean, autoCols?:boolean):IFrame;

    getRangeValues(range:ISheetRange):Map<SheetLocationKey, IValue>;

    copyRangeWithFormulas(from:ISheetRange, to:ISheetLocation, times:number):Promise<void>;

    setCellFormula(location:ISheetLocation, newValue:IValue):Promise<IValue>;

    getCellFormulaText(location:ISheetLocation, formulaKind:FormulaKind):string | null;

    setCellFormulasInBulk(locations:ISheetLocation[], newValues:IValue[]):Promise<IValue>

    setCellValue(location:ISheetLocation, newValue:IValue):IValue;

    expandRange(range:ISheetRange, direction:SheetDirection, side:SheetRangeSide, amount:number):void;

    collapseRange(range:ISheetRange, direction:SheetDirection, side:SheetRangeSide, amount:number):void;

    getRangeIterator(range:ISheetRange):ISheetRangeIterator;

    reEvaluateLazyCell(location:ISheetLocation):Promise<IsCellReEvaluationDone>;

    findFirstBlankRowBelow(location:ISheetLocation):ISheetLocation

    addRowsBelow(location:ISheetLocation, values:IFrame):Promise<void>;

    readValuesUntilEmptyCell(startAt:ISheetLocation):IFrame;

    defineRangeBoundary(startAt:ISheetRange, offset?:number, numRows?:number, columnToUse?:number):ISheetRange | IValue;

    clearRange(fromCellInclusive:ISheetLocation, toCellInclusive:ISheetLocation, keepBindings:boolean):Promise<void>;

    deleteRows(fromRowInclusive:ISheetLocation, toRowInclusive:ISheetLocation, keepBindings:boolean):Promise<void>;

    deleteColumns(fromColumnInclusive:ISheetLocation, toColumnInclusive:ISheetLocation, keepBindings:boolean):Promise<void>;

    insertFrameRows(targetLocation:ISheetLocation, insertIndex:number, frameToAdd:IFrame, addAll:boolean):ABoolean | AError;

    updateFrameRow(targetLocation:ISheetLocation, updateIndex:number, updateFrame:IFrame):ABoolean | AError;

    deleteFrameRows(targetLocation:ISheetLocation, deleteIndexes:Array<number>, keepBindings:boolean):ABoolean | AError;

    insertFrameColumns(targetLocation:ISheetLocation, insertIndex:number, columnName:string, columnType:Type):ABoolean | AError;

    updateFrameColumn(targetLocation:ISheetLocation, updateIndex:number, newColumnName:string, newColumnType?:Type):ABoolean | AError;

    deleteFrameColumns(targetLocation:ISheetLocation, deleteIndexes:Array<number>):ABoolean | AError;

    getSlotId():SlotId;

    getSheetId():SheetId;

    getCellLocation():ISheetLocation;

    getCellCurrentValue():IValue;

    findCellLocationWithSheetName(columnIndex:number, rowIndex:number, sheetName?:string):ISheetLocation | Empty;

    findCellLocationWithSheetId(columnIndex:number, rowIndex:number, sheetId?:SheetId):ISheetLocation | Empty;

    evalFormula(formulaText:string, formulaKind:FormulaKind, namedArguments:NamedArguments, useCurrentContext:boolean):Promise<IValue>;

    evalExpr(exprText:string, namedArguments:NamedArguments, useCurrentContext:boolean):Promise<IValue>;

    getEventFrame():IFrame;

    startLiveCell(location:ISheetLocation):Promise<boolean>;

    stopLiveCell(location:ISheetLocation):Promise<boolean>;

    restartLiveCell(location:ISheetLocation):Promise<boolean>;

    isSheetExists(sheetId:SheetId):boolean;

    getRowsCount(sheetId:SheetId):number;

    getColumnsCount(sheetId:SheetId):number;

    getWorkbookId(sheetId:SheetId):WorkbookId;

    getCellSettings(location:ISheetLocation):ISheetCellSettings;

    getCellFormat(location:ISheetLocation):ISheetCellFormat;

    setCellSettings(location:ISheetLocation, newCellSettings:ISheetCellSettings):void;

    setCellFormat(location:ISheetLocation, newCellFormatId:string):void;

    getRowWidth(location:ISheetLocation):number;

    getDesignName():string;

    convertToIValue(value:IValue | ISheetValue):IValue | IValue[];

    getLocationHash():string;

    getViewportInfoFrame():IFrame;
}

export type OnTimerFn = () => (void);

export interface ITimerContext
{
    registerOneTime(intervalMs:number, timerFn:OnTimerFn):void;

    registerRepeating(intervalMs:number, timerFn:OnTimerFn, slotId:SlotId):void;

    stopTimerForSlot(slotId:SlotId):void;
}

export interface ISurfaceContext
{
    getBoxIdByName(boxName:string):BoxId;

    setActivePage(pageName:string, isAnimated:boolean):boolean;

    getActivePageName():string;

    showOverlay(pageName:string, isAnimated:boolean):boolean;

    hideOverlay(name:string, hideAllAbove:boolean, isAnimated:boolean):boolean;

    toggleOverlay(name:string, hideAllAbove:boolean, isAnimated:boolean):boolean;

    hideTopmostOverlay(isAnimated:boolean):boolean;

    hideAllOverlays(isAnimated:boolean):boolean;

    addComponent(pageName:string,
                 parentComponentName:string,
                 componentType:string,
                 componentName?:string,
                 xOffsetInParent?:number,
                 yOffsetInParent?:number,
                 width?:number,
                 height?:number):Promise<boolean | Error>;

    copyComponent(srcPageName:string,
                  srcComponentName:string,
                  destPageName:string,
                  destParentComponentName:string,
                  newComponentName:string,
                  keepBindings:boolean,
                  xOffsetInParent?:number,
                  yOffsetInParent?:number,
                  width?:number,
                  height?:number):Promise<boolean | Error>;

    setBinding(pageName:string,
               componentName:string,
               propOrEventFullPath:string,
               sheetName:string,
               cellLocation:ISheetLocation,
               pathInFrame:string,
               fixedDirection:string,
               useFormat:boolean,
               direction:string,
               itemsCount:number):boolean | Error;

    removeAllComponents(pageName:string):boolean | Error;

    removeComponents(pageName:string, parentComponentName:string):boolean | Error;

    removeComponent(pageName:string, componentName:string):boolean | Error;

    animate(pageName:string,
            componentName:string,
            repeaterLevels:Array<number>,
            animationType:AnimationType,
            options:IFrame):Promise<boolean | Error>;

    stopAnimations(pageName:string,
                  componentName:string,
                  repeaterLevels:Array<number>):boolean | Error;
}

export enum FunctionFeature
{
    ModifiesSpreadsheet,
    UsesTimers,
    ArrayFormula,
}

export interface ISheetAPI
{
    getSheetContext():ISheetContext;

    getTimerContext():ITimerContext;

    getSurfaceContext():ISurfaceContext;
}
